/* eslint-disable max-len */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
} from 'reactstrap';

import GoToTop from '../../components/GoToTop';
import { useAuth } from '../../hooks/auth';
import LayoutAdmin from '../../layouts/admin';
import api from '../../services/api';

const EMPTY_LINKS = {
  painel: '',
  telao: '',
  telaoPrivado: '',
  iniciarVotacao: '',
};

export default function Painel() {
  const { loggedUser, isAuthenticatedAdmin } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(null);
  const [links, setLinks] = useState(EMPTY_LINKS);
  const [expiresAt, setExpiresAt] = useState(null);
  const [ttlHours, setTtlHours] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchLinks = useCallback(async () => {
    setLoadingLinks(true);
    setErrorMessage(null);
    try {
      const { data } = await api.get('/painel/access-links');
      setLinks(data.links || EMPTY_LINKS);
      setExpiresAt(data.expiresAt || null);
      setTtlHours(data.ttlHours || null);
    } catch (error) {
      const message = error?.response?.data?.message || 'Não foi possível carregar os links dos painéis.';
      setErrorMessage(message);
      setLinks(EMPTY_LINKS);
    } finally {
      setLoadingLinks(false);
    }
  }, []);

  const copyToClipboard = useCallback((url, key) => {
    if (!url) return;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }, []);

  const handleRegenerate = useCallback(async () => {
    setActionLoading('regenerate');
    setErrorMessage(null);
    try {
      const { data } = await api.post('/painel/access-links/regenerate');
      setLinks(data.links || EMPTY_LINKS);
      setExpiresAt(data.expiresAt || null);
      setTtlHours(data.ttlHours || null);
    } catch (error) {
      const message = error?.response?.data?.message || 'Não foi possível regenerar os links.';
      setErrorMessage(message);
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleRevokeAll = useCallback(async () => {
    setActionLoading('revoke');
    setErrorMessage(null);
    try {
      await api.delete('/painel/access-links');
      setLinks(EMPTY_LINKS);
      setExpiresAt(null);
      await fetchLinks();
    } catch (error) {
      const message = error?.response?.data?.message || 'Não foi possível revogar os links.';
      setErrorMessage(message);
    } finally {
      setActionLoading(null);
    }
  }, [fetchLinks]);

  useEffect(() => {
    if (!isAuthenticatedAdmin) return;
    if (loggedUser?.type !== undefined && loggedUser?.type !== 'super-admin') {
      navigate('/admin/', { replace: true });
    }
  }, [isAuthenticatedAdmin, loggedUser?.type, navigate]);

  useEffect(() => {
    if (loggedUser?.type === 'super-admin') {
      fetchLinks();
    }
  }, [loggedUser?.type, fetchLinks]);

  const isSuperAdmin = loggedUser?.type === 'super-admin';
  const isLoadingUser = isAuthenticatedAdmin && loggedUser?.type === undefined;

  if (isLoadingUser) {
    return (
      <LayoutAdmin>
        <Container className="d-flex justify-content-center align-items-center py-5">
          <Spinner type="grow" color="primary" />
          <span className="ms-2">Carregando...</span>
        </Container>
      </LayoutAdmin>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  const expirationLabel = expiresAt
    ? new Date(expiresAt).toLocaleString('pt-BR')
    : null;

  return (
    <LayoutAdmin>
      <Container>
        <GoToTop />
        <Row>
          <Col>
            <Card className="mt-5">
              <CardHeader>
                <div className="d-flex justify-content-center">
                  <h2>Painel</h2>
                </div>
                <p className="text-muted text-center mb-0 mt-2">
                  Acesse as ferramentas de votação para conduzir sessões e exibir o telão.
                </p>
              </CardHeader>
              <CardBody>
                {errorMessage ? (
                  <Alert color="danger">{errorMessage}</Alert>
                ) : null}

                <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                  <Button
                    color="secondary"
                    outline
                    disabled={loadingLinks || actionLoading}
                    onClick={handleRegenerate}
                  >
                    {actionLoading === 'regenerate' ? 'Regenerando...' : 'Regenerar links'}
                  </Button>
                  <Button
                    color="danger"
                    outline
                    disabled={loadingLinks || actionLoading}
                    onClick={handleRevokeAll}
                  >
                    {actionLoading === 'revoke' ? 'Revogando...' : 'Revogar todos'}
                  </Button>
                </div>

                {expirationLabel ? (
                  <p className="text-muted text-center small mb-4">
                    {`Links válidos até ${expirationLabel}${ttlHours ? ` (TTL: ${ttlHours}h)` : ''}`}
                  </p>
                ) : null}

                {loadingLinks ? (
                  <div className="text-center py-4">
                    <Spinner type="grow" color="primary" />
                    <span className="ms-2">Carregando links autorizados...</span>
                  </div>
                ) : (
                  <Row className="g-4">
                    <Col md={6}>
                      <Card className="h-100 shadow-sm">
                        <CardBody className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                          <i className="fa-solid fa-chart-line fa-3x text-primary mb-3" />
                          <h4 className="mb-2">Painel de votação</h4>
                          <p className="text-muted mb-4">
                            Conduza e acompanhe votações em tempo real.
                          </p>
                          <Button
                            color="primary"
                            size="lg"
                            tag="a"
                            href={links.painel || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!links.painel}
                          >
                            Acessar o painel de votação
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="mt-2"
                            disabled={!links.painel}
                            onClick={() => copyToClipboard(links.painel, 'painel')}
                          >
                            <i className="fa-regular fa-copy me-1" />
                            {copied === 'painel' ? 'Copiado!' : 'Copiar link'}
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-100 shadow-sm">
                        <CardBody className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                          <i className="fa-solid fa-mobile-screen-button fa-3x text-primary mb-3" />
                          <h4 className="mb-2">Iniciar votação</h4>
                          <p className="text-muted mb-4">
                            App exclusivo para acompanhar a prévia e iniciar a votação no celular.
                          </p>
                          <Button
                            color="primary"
                            size="lg"
                            tag="a"
                            href={links.iniciarVotacao || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!links.iniciarVotacao}
                          >
                            Acessar iniciar votação
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="mt-2"
                            disabled={!links.iniciarVotacao}
                            onClick={() => copyToClipboard(links.iniciarVotacao, 'iniciar-votacao')}
                          >
                            <i className="fa-regular fa-copy me-1" />
                            {copied === 'iniciar-votacao' ? 'Copiado!' : 'Copiar link'}
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-100 shadow-sm">
                        <CardBody className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                          <i className="fa-solid fa-tv fa-3x text-primary mb-3" />
                          <h4 className="mb-2">Telão de votação</h4>
                          <p className="text-muted mb-4">
                            Exiba o telão na sessão para visualização pelos presentes.
                          </p>
                          <Button
                            color="primary"
                            size="lg"
                            tag="a"
                            href={links.telao || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!links.telao}
                          >
                            Acessar o telão de votação
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="mt-2"
                            disabled={!links.telao}
                            onClick={() => copyToClipboard(links.telao, 'telao')}
                          >
                            <i className="fa-regular fa-copy me-1" />
                            {copied === 'telao' ? 'Copiado!' : 'Copiar link'}
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-100 shadow-sm">
                        <CardBody className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                          <i className="fa-solid fa-lock fa-3x text-primary mb-3" />
                          <h4 className="mb-2">Telão de votação privado</h4>
                          <p className="text-muted mb-4">
                            Exiba o telão privado na sessão para visualização restrita aos presentes.
                          </p>
                          <Button
                            color="primary"
                            size="lg"
                            tag="a"
                            href={links.telaoPrivado || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!links.telaoPrivado}
                          >
                            Acessar o telão de votação privado
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="mt-2"
                            disabled={!links.telaoPrivado}
                            onClick={() => copyToClipboard(links.telaoPrivado, 'telao-privado')}
                          >
                            <i className="fa-regular fa-copy me-1" />
                            {copied === 'telao-privado' ? 'Copiado!' : 'Copiar link'}
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </LayoutAdmin>
  );
}
